<template>
  <div class="min-h-screen">
    <Header />
    
    <!-- Hero Section -->
    <section class="page-hero-shell pt-32 pb-20">
      <div class="absolute inset-0 page-overlay-layer space-pattern opacity-45"></div>
      <div class="hero-orb page-overlay-layer hero-orb-a"></div>
      <div class="hero-orb page-overlay-layer hero-orb-b"></div>
      
      <div class="page-content-layer max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <div class="hero-badge mb-6">
            <span>增长实战档案</span>
          </div>
          <h1 class="text-5xl md:text-7xl font-bold mb-6">
            <span class="gradient-text">客户案例</span>
          </h1>
          <p class="text-xl text-gray-700 dark:text-dark-muted max-w-3xl mx-auto">
            真实的成功故事，见证我们如何帮助企业实现数字化增长
          </p>
        </div>
      </div>
    </section>

    <!-- Stats Section -->
    <section class="py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid md:grid-cols-4 gap-8 section-panel p-6">
          <div class="text-center">
            <div class="text-5xl font-bold gradient-text mb-2">500+</div>
            <div class="text-gray-700 dark:text-dark-muted">服务客户</div>
          </div>
          <div class="text-center">
            <div class="text-5xl font-bold gradient-text mb-2">98%</div>
            <div class="text-gray-700 dark:text-dark-muted">客户满意度</div>
          </div>
          <div class="text-center">
            <div class="text-5xl font-bold gradient-text mb-2">+156%</div>
            <div class="text-gray-700 dark:text-dark-muted">平均转化提升</div>
          </div>
          <div class="text-center">
            <div class="text-5xl font-bold gradient-text mb-2">+234%</div>
            <div class="text-gray-700 dark:text-dark-muted">平均ROI提升</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Cases Grid -->
    <section class="py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="section-title gradient-text mb-16">成功案例</h2>
        
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="(caseItem, index) in cases"
               :key="index"
               class="card card-hover group visual-surface">
            <div class="relative mb-6 overflow-hidden rounded-xl">
              <CaseIllustration
                :type="caseItem.illustrationType || 'targeting'"
                :variant="caseItem.illustrationKey || `case-${caseItem.id}`"
              />
            </div>

            <div class="mb-4">
              <span class="inline-block px-3 py-1 bg-cosmic/20 text-cosmic-adaptive rounded-full text-xs">
                {{ caseItem.industry }}
              </span>
            </div>

            <h3 class="text-xl font-bold mb-3 text-white">{{ caseItem.title }}</h3>
            <p class="text-gray-700 dark:text-dark-muted text-sm mb-6">{{ caseItem.description }}</p>

            <div class="border-t border-black/10 dark:border-white/10 pt-4 mb-4">
              <div class="text-sm text-gray-700 dark:text-dark-muted mb-2">核心成果</div>
              <div v-if="caseItem.metrics && caseItem.metrics.length >= 2" class="grid grid-cols-2 gap-2">
                <div class="bg-gray-100 dark:bg-white/5 rounded-lg p-2 text-center">
                  <div class="text-lg font-bold text-cosmic-adaptive">{{ caseItem.metrics[0]?.value }}</div>
                  <div class="text-xs text-gray-700 dark:text-dark-muted">{{ caseItem.metrics[0]?.label }}</div>
                </div>
                <div class="bg-gray-100 dark:bg-white/5 rounded-lg p-2 text-center">
                  <div class="text-lg font-bold text-nebula-adaptive">{{ caseItem.metrics[1]?.value }}</div>
                  <div class="text-xs text-gray-700 dark:text-dark-muted">{{ caseItem.metrics[1]?.label }}</div>
                </div>
              </div>
            </div>

            <NuxtLink :to="`/cases/${caseItem.id}`" class="w-full py-3 border border-primary-30 text-primary text-center rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors text-sm block">
              查看详情
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- Featured Case -->
    <section class="py-20 relative">
      <div class="absolute inset-0 page-overlay-layer bg-gradient-to-r from-cosmic/5 via-nebula/5 to-stellar/5"></div>
      
      <div class="page-content-layer max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="section-title gradient-text mb-16">精选案例</h2>
        
        <div class="glass-card p-8 md:p-12 border border-white/10 visual-surface">
          <div class="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div class="mb-6">
                <span class="inline-block px-4 py-2 bg-cosmic/20 text-cosmic-adaptive rounded-full text-sm">
                  电商平台
                </span>
              </div>
              
              <h3 class="text-3xl font-bold mb-6 text-white">某知名电商平台</h3>
              <p class="text-gray-700 dark:text-dark-muted mb-8">
                该平台面临用户获取成本高、转化率低的问题。通过我们的DSP+DMP整合方案，
                实现了精准用户定位和智能投放优化。
              </p>
              
              <div class="space-y-4 mb-8">
                <div class="flex items-start">
                  <div class="flex-shrink-0 w-8 h-8 rounded-lg bg-cosmic/20 flex items-center justify-center mr-4">
                    <svg class="w-4 h-4 text-cosmic" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-white font-semibold mb-1">精准用户定向</h4>
                    <p class="text-gray-700 dark:text-dark-muted text-sm">基于DMP用户画像，精准定位目标用户群体</p>
                  </div>
                </div>
                
                <div class="flex items-start">
                  <div class="flex-shrink-0 w-8 h-8 rounded-lg bg-nebula/20 flex items-center justify-center mr-4">
                    <svg class="w-4 h-4 text-nebula" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-white font-semibold mb-1">智能投放优化</h4>
                    <p class="text-gray-700 dark:text-dark-muted text-sm">AI算法自动优化投放策略，提升转化率</p>
                  </div>
                </div>
                
                <div class="flex items-start">
                  <div class="flex-shrink-0 w-8 h-8 rounded-lg bg-stellar/20 flex items-center justify-center mr-4">
                    <svg class="w-4 h-4 text-stellar" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-white font-semibold mb-1">实时数据监控</h4>
                    <p class="text-gray-700 dark:text-dark-muted text-sm">24/7实时监控，及时调整投放策略</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="glass-card p-8 bg-white/5 visual-surface">
              <h4 class="text-xl font-bold mb-6 text-white">项目成果</h4>
              
              <div class="space-y-6">
                <div>
                  <div class="flex justify-between mb-2">
                    <span class="text-gray-700 dark:text-dark-muted">转化率提升</span>
                    <span class="text-cosmic-adaptive font-bold">+180%</span>
                  </div>
                  <div class="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 transition-colors duration-200">
                    <div class="bg-gradient-to-r from-cosmic to-nebula h-2 rounded-full" style="width: 90%"></div>
                  </div>
                </div>

                <div>
                  <div class="flex justify-between mb-2">
                    <span class="text-gray-700 dark:text-dark-muted">获客成本降低</span>
                    <span class="text-nebula-adaptive font-bold">-45%</span>
                  </div>
                  <div class="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 transition-colors duration-200">
                    <div class="bg-gradient-to-r from-nebula to-stellar h-2 rounded-full" style="width: 55%"></div>
                  </div>
                </div>

                <div>
                  <div class="flex justify-between mb-2">
                    <span class="text-gray-700 dark:text-dark-muted">ROI提升</span>
                    <span class="text-stellar-adaptive font-bold">+320%</span>
                  </div>
                  <div class="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 transition-colors duration-200">
                    <div class="bg-gradient-to-r from-stellar to-cosmic h-2 rounded-full" style="width: 95%"></div>
                  </div>
                </div>
              </div>
              
              <div class="mt-8 p-4 bg-cosmic/10 rounded-lg">
                <div class="text-sm text-gray-700 dark:text-dark-muted mb-1">客户评价</div>
                <p class="text-gray-700 dark:text-dark-muted italic">
                  "MAGIC的专业团队和先进技术帮助我们实现了业务突破性增长，转化率提升了180%，ROI提升了320%！"
                </p>
                <div class="mt-3 text-sm text-gray-700 dark:text-dark-muted">
                  — 某知名电商平台市场总监
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-32">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="text-4xl md:text-5xl font-bold mb-6 gradient-text">成为下一个成功案例</h2>
        <p class="text-xl text-gray-700 dark:text-dark-muted mb-12">
          让我们帮助您的企业实现数字化增长，创造属于您的成功故事
        </p>
        
        <button @click="contactUs" class="btn-primary">
          预约咨询
        </button>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script setup>
const cases = [
  {
    id: '1',
    icon: '🛒',
    iconName: 'shopping',
    illustrationType: 'shopping',
    illustrationKey: 'case-1',
    title: '某知名电商平台',
    industry: '电商行业',
    description: '通过DSP+DMP整合方案，实现精准用户定位和智能投放优化',
    metrics: [
      { value: '+180%', label: '转化率提升' },
      { value: '-45%', label: '获客成本' }
    ]
  },
  {
    id: '2',
    icon: '🎮',
    iconName: 'gaming',
    illustrationType: 'gaming',
    illustrationKey: 'case-2',
    title: '某游戏公司',
    industry: '游戏行业',
    description: '通过DSP精准投放，用户获取成本降低50%，日活用户增长200%',
    metrics: [
      { value: '-50%', label: '获客成本' },
      { value: '+200%', label: '日活增长' }
    ]
  },
  {
    id: '3',
    icon: '📚',
    iconName: 'education',
    illustrationType: 'education',
    illustrationKey: 'case-3',
    title: '某在线教育平台',
    industry: '教育培训',
    description: '私域流量运营方案，学员留存率提升180%，复购率提升120%',
    metrics: [
      { value: '+180%', label: '留存率' },
      { value: '+120%', label: '复购率' }
    ]
  },
  {
    id: '4',
    icon: '🛒',
    iconName: 'shopping',
    illustrationType: 'shopping',
    illustrationKey: 'case-4',
    title: '某电商平台',
    industry: '电子商务',
    description: 'SSP+DSP整合方案，广告收益提升80%，用户转化率提升160%',
    metrics: [
      { value: '+80%', label: '广告收益' },
      { value: '+160%', label: '转化率' }
    ]
  },
  {
    id: '5',
    icon: '🏥',
    iconName: 'health',
    illustrationType: 'health',
    illustrationKey: 'case-5',
    title: '某医疗APP',
    industry: '医疗健康',
    description: '海外ADX跨境投放，国际用户增长300%，品牌知名度大幅提升',
    metrics: [
      { value: '+300%', label: '国际用户' },
      { value: '+250%', label: '品牌曝光' }
    ]
  },
  {
    id: '6',
    icon: '🚗',
    iconName: 'automotive',
    illustrationType: 'automotive',
    illustrationKey: 'case-6',
    title: '某汽车品牌',
    industry: '汽车行业',
    description: '一站式开户服务，快速启动投放，销售线索增长220%',
    metrics: [
      { value: '+220%', label: '销售线索' },
      { value: '-40%', label: '投放成本' }
    ]
  }
]

const contactUs = () => {
  window.location.href = '/about#contact'
}
</script>
